import clutch from "../assets/img/clutch.webp";
import rating from "../assets/img/rating.webp";
import trustpilot from "../assets/img/trustpilot.webp";
import footerImg from "../assets/img/footer.webp";
import GlitchText from "./GlitchText";
import { HashLink } from "react-router-hash-link";
import { useCallback } from "react";
import siteData from "../data/siteData";

const Footer = () => {
  const scrollWithOffset = useCallback((el) => {
    const yOffset = -90;
    const rect = el.getBoundingClientRect();
    const y = rect.top + window.pageYOffset + yOffset;

    if (window.lenis && typeof window.lenis.scrollTo === "function") {
      window.lenis.scrollTo(y);
    } else {
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, []);

  return (
    <footer className="footer">
      <div className="footer-top bg-white">
        <div className="w-full max-w-7xl mx-auto px-[20px] lg:px-16">
          <div className="flex xl:flex-nowrap flex-wrap justify-between pt-[65px] pb-[60px] gap-14 relative">
            <div className="xl:w-[35%] lg:w-[55%] w-full ">
              <h3 className="md:text-[30px] text-[22px] font-medium text-blue font-unbounded">
                Let’s Automate One <span className="text-primary"> Workflow </span>First.
              </h3>
              <p className="text-lg font-medium text-blue font-poppins">
                Tell us which repetitive process is slowing your team down. We’ll review the workflow, identify practical automation opportunities, and recommend the best place to start.
              </p>
              <div className="flex sm:flex-row flex-col gap-5 pt-[30px] flex-wrap">
                <div className="relative">
                  <HashLink
                    to="/#ai-automation-services"
                    smooth
                    scroll={scrollWithOffset}
                    className="w-fit relative overflow-hidden flex items-center justify-center bg-primary px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group"
                  >
                    <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap">
                      Request a Free Automation Audit
                    </span>
                    <span className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full transition-all duration-700 group-hover:w-[30rem] group-hover:h-[15rem] -translate-x-1/2 -translate-y-1/2"></span>
                  </HashLink>
                </div>
                <div className="relative">
                  <HashLink
                    to="/#projects"
                    smooth
                    scroll={scrollWithOffset}
                    className="w-fit relative overflow-hidden flex items-center justify-center bg-blue px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group"
                  >
                    <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap">
                      View Case Studies
                    </span>
                    <span className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full transition-all duration-700 group-hover:w-[15rem] group-hover:h-[15rem] -translate-x-1/2 -translate-y-1/2"></span>
                  </HashLink>
                </div>
              </div>
            </div>

            <div className="xl:w-[15%] lg:w-[35%] w-full">
              <h3 className="text-[20px] font-bold text-blue mb-3">
                Quick Links
              </h3>
              <ul className="flex flex-col gap-1 text-blue text-base" aria-label="Footer navigation links">
                <li>
                  <HashLink
                    scroll={scrollWithOffset}
                    smooth
                    to="/#projects"
                    className="relative inline-block whitespace-nowrap after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
                  >
                    Projects
                  </HashLink>
                </li>
                <li>
                  <HashLink
                    scroll={scrollWithOffset}
                    smooth
                    to="/#faqs"
                    className="relative inline-block whitespace-nowrap after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
                  >
                    FAQ
                  </HashLink>
                </li>
                <li>
                  <HashLink
                    scroll={scrollWithOffset}
                    smooth
                    to="/blog"
                    className="relative inline-block font-peachi text-lg text-lightBlack hover:text-primary transition after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left"
                  >
                    Blog
                  </HashLink>
                </li>
                <li>
                  <a
                    href="/services/ai-automation"
                    className="relative inline-block text-lg text-lightBlack hover:text-primary transition after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left"
                  >
                    AI Automation
                  </a>
                </li>
                <li>
                  <a
                    href="/services/web-mobile-development"
                    className="relative inline-block whitespace-nowrap after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
                  >
                    Web & Mobile Apps
                  </a>
                </li>
                <li>
                  <a
                    href="/services/saas-erp-development"
                    className="relative inline-block whitespace-nowrap after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
                  >
                    SaaS & ERP
                  </a>
                </li>
                <li>
                  <a
                    href="/services/desktop-software"
                    className="relative inline-block whitespace-nowrap after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
                  >
                    Desktop Software
                  </a>
                </li>

                <li>
                  <a
                    href="/contact"
                    className="relative inline-block whitespace-nowrap after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
                  >
                    Contact
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy-policy"
                    className="relative inline-block whitespace-nowrap after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/terms-of-service"
                    className="relative inline-block whitespace-nowrap after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
                  >
                    Terms
                  </a>
                </li>
              </ul>
            </div>

            <div className="xl:w-[25%] lg:w-1/2 w-full">
              <h3 className="text-[20px] font-bold text-blue mb-3">Office</h3>
              <p className="text-base text-blue">{siteData.addressDisplay}</p>
              <a
                href={`tel:${siteData.phoneHref}`}
                className="mt-3 inline-flex text-base font-semibold text-primary hover:underline"
              >
                {siteData.phoneDisplay}
              </a>
              <p className="mt-3 text-sm text-blue/80 font-poppins">
                Serving businesses worldwide.
              </p>
              <div className="flex items-center gap-5 pt-[43px]">
                <div className="flex flex-col gap-3">
                  <img
                    src={clutch}
                    alt="Clutch"
                    className="h-[25px] w-[87px] object-contain"
                    loading="lazy"
                    width="87"
                    height="25"
                  />
                  <img
                    src={rating}
                    alt="Rating"
                    className="h-[20px] w-full object-contain"
                    loading="lazy"
                    width="150"
                    height="20"
                  />
                </div>
                <img
                  src={trustpilot}
                  alt="Trustpilot"
                  className="h-[67px]"
                  loading="lazy"
                  width="140"
                  height="67"
                />
              </div>
            </div>

            <div className="w-[25%] xl:block hidden relative">
              <img
                src={footerImg}
                alt="QBITS9 AI Automation – decorative footer illustration"
                className="mt-[-165px] absolute bottom-[-60px]"
                loading="lazy"
                width="460"
                height="460"
              />
            </div>
          </div>
        </div>

        <div className="py-[15px] border-y border-[#D8D8D8] bg-white">
          <div className="w-full max-w-7xl mx-auto px-[20px] lg:px-16 flex md:flex-row flex-col gap-6 justify-between">
            <p className="text-base text-blue font-poppins">
              ©2026 QBits9
            </p>
            <div className="flex flex-col gap-2 text-base text-blue font-poppins">
              {/* <a href="mailto:hello@qbits9.com" className="text-primary hover:underline">
                hello@qbits9.com
              </a> */}
              <span>Designed and developed by QBits9</span>
            </div>
          </div>
        </div>
      </div>

      <p className="sr-only">
        Qbits9 is an AI automation company providing intelligent automation solutions, software development services, and workflow optimization systems for businesses worldwide.
      </p>

      <GlitchText />
    </footer>
  );
};

export default Footer;
